import { useMemo } from 'react';
import { useCreatorSearch } from '@round/creatorbase-helpers/api-hooks';
import { useInstagramUserImages } from '@round/platforms/instagram';
import { useTikTokUserImages } from '@round/platforms/tiktok';
import {
    extractUserIdsFromData,
    transformDataWithUserImages,
} from 'Modules/Plans/CreatorSearch/utils/creatorSearchImageUtils';
import { TableRow } from 'Modules/Plans/CreatorSearch/utils/creatorSearchUtils';

type Args = {
    search: string;
    targetPlatform: 'tiktok' | 'instagram';
    clientId: number | undefined;
    enabled: boolean;
};

export default function useSearchAccounts({ search, targetPlatform, clientId, enabled }: Args) {
    const trimmed = search.trim();
    const searchQuery = useCreatorSearch(
        {
            page: 1,
            page_size: 10,
            search: trimmed,
            order_by_desc: 'created',
            platforms: targetPlatform,
            client_id: clientId,
        },
        { enabled: enabled && trimmed.length > 0 && typeof clientId === 'number' }
    );

    const ids = useMemo(() => {
        const results = searchQuery.data?.results ?? [];
        return extractUserIdsFromData(results);
    }, [searchQuery.data]);

    const tiktok = useTikTokUserImages(ids.tiktokUserIds, {
        enabled: ids.tiktokUserIds.length > 0,
    });
    const instagram = useInstagramUserImages(ids.instagramUserIds, {
        enabled: ids.instagramUserIds.length > 0,
    });

    const rows = useMemo<TableRow[]>(() => {
        const results = searchQuery.data?.results ?? [];
        return transformDataWithUserImages(results, tiktok.data ?? [], instagram.data ?? []);
    }, [searchQuery.data, tiktok.data, instagram.data]);

    return {
        rows,
        isFetching: searchQuery.isFetching,
        error: searchQuery.error,
    };
}
